// Assessment submission helpers

export type SectionSlug =
  | 'soft-skill'
  | 'personality'
  | 'work-style'
  | 'work-motivators'
  | 'basic-expectations'
  | 'work-qualities'
  | 'domain-knowledge';

export interface QuestionWithField {
  id: string;
  field?: string;
  type: string;
  category?: string;
  options?: any[];
  select_count?: number;
  hasOthersOption?: boolean;
  othersField?: string;
  activities?: string[];
  statements?: string[];
  items?: any[];
  section?: SectionSlug;
  backendId?: number;
}

const normalizeFieldForAPI = (field: string): string => {
  const fieldMap: Record<string, string> = {
    'leadership_q1_options': 'positive_leadership_q1',
    'leadership_q2_choice': 'positive_leadership_q2_option',
    'leadership_q3_rating': 'negative_leadership_q1_rating',
    'leadership_q4_rating': 'negative_leadership_q2_rating',
  };
  if (fieldMap[field]) return fieldMap[field];
  if (field.startsWith('agile_work_ethic_')) {
    return field.replace('agile_work_ethic_', 'agility_work_ethic_');
  }
  if (field.startsWith('tech_curiosity_')) {
    return field.replace('tech_curiosity_', 'technology_curiosity_');
  }
  return field;
};

// Competency prefix → backend payload key.
// The field names use short prefixes (e.g. `creative_q1_...`) but the backend
// expects fully-qualified competency keys (e.g. `creative_thinking`).
const COMPETENCY_KEY_MAP: Record<string, string> = {
  analytical: 'analytical_thinking',
  creative: 'creative_thinking',
  admin: 'administration',
  tech_curiosity: 'technology_curiosity',
  agility: 'agility_work_ethic',
  agile_work_ethic: 'agility_work_ethic',
  learning: 'learning_curiosity',
};

const resolveCompetencyKey = (parsedCompetency: string): string =>
  COMPETENCY_KEY_MAP[parsedCompetency] || parsedCompetency;

const indexToLetter = (index: number): string => String.fromCharCode(65 + index);
const indexToLowerLetter = (index: number): string => String.fromCharCode(97 + index);

const findOptionIndex = (options: any[] | undefined, value: any): number => {
  if (!options) return -1;
  return options.findIndex(
    (opt: any) =>
      opt.text === value ||
      opt.label === value ||
      opt.value === value ||
      (opt.score !== undefined && String(opt.score) === String(value))
  );
};

const normalizeSlugValue = (value: any): string | null => {
  if (value === undefined || value === null || value === '') return null;
  return String(value).toLowerCase().trim().replace(/\s+/g, '_');
};

const resolveOptionValue = (question: QuestionWithField | undefined, value: any): string | null => {
  if (!question?.options || question.options.length === 0) {
    return normalizeSlugValue(value);
  }

  if (typeof value === 'number' && value > 0) {
    const byIndex = question.options[value - 1];
    return normalizeSlugValue(byIndex?.value ?? byIndex?.text ?? byIndex?.label);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      const numericIndex = Number(trimmed);
      const byIndex = question.options[numericIndex - 1];
      return normalizeSlugValue(byIndex?.value ?? byIndex?.text ?? byIndex?.label);
    }

    const matchedOption = question.options.find(
      (opt: any) =>
        opt.text === value ||
        opt.label === value ||
        opt.value === value ||
        normalizeSlugValue(opt.text) === normalizeSlugValue(value) ||
        normalizeSlugValue(opt.label) === normalizeSlugValue(value) ||
        normalizeSlugValue(opt.value) === normalizeSlugValue(value)
    );
    if (matchedOption) {
      return normalizeSlugValue(matchedOption.value ?? matchedOption.text ?? matchedOption.label);
    }
  }

  if (typeof value === 'object') {
    return normalizeSlugValue(value.value ?? value.text ?? value.label ?? value.name);
  }

  return normalizeSlugValue(value);
};

const getSectionQuestions = (questions: QuestionWithField[], section: SectionSlug): QuestionWithField[] =>
  questions.filter((question) => question.section === section);


interface ParsedField {
  competency: string;
  qNum: number;
  answerType?: 'statement' | 'rating' | 'choice' | 'ranking' | 'option' | 'options';
}

const parseField = (field: string): ParsedField | null => {
  let m = field.match(/^personality_question_(\d+)(?:_(option|rating))?$/);
  if (m) {
    return { competency: 'personality', qNum: parseInt(m[1], 10), answerType: m[2] as any };
  }
  m = field.match(/^(.+)_q(\d+)(?:_(statement|rating|choice|ranking|option|options))?$/);
  if (m) {
    return { competency: m[1], qNum: parseInt(m[2], 10), answerType: m[3] as any };
  }
  return null;
};

const inferSectionFromField = (field: string): SectionSlug | null => {
  if (field.startsWith('domain_g')) return 'domain-knowledge';
  if (field.startsWith('personality_')) return 'personality';
  if (field.startsWith('work_motivators') || field.startsWith('motivators')) return 'work-motivators';
  if (field.startsWith('work_style_')) return 'work-style';
  if (field.startsWith('basic_expectations_')) return 'basic-expectations';
  if (field.startsWith('workplace_qualities') || field.startsWith('work_qualities') || field.startsWith('qualities')) return 'work-qualities';
  const parsed = parseField(field);
  if (!parsed) return null;
  const workStyleComps = ['prioritizing', 'priority', 'visualizing', 'planning', 'coordinating'];
  const baseComp = parsed.competency.replace(/_(entry|associate|mid|senior)$/, '');
  if (workStyleComps.includes(baseComp)) return 'work-style';
  const softSkillComps = [
    'analytical_thinking', 'analytical', 'creative_thinking', 'creative', 'empathy',
    'learning_curiosity', 'learning_agility', 'technology_curiosity', 'tech_curiosity',
    'administration', 'admin', 'agility_work_ethic', 'agility', 'agile_work_ethic',
    'eloquence', 'leadership_and_management', 'leadership', 'positive_leadership',
    'negative_leadership', 'persuasion', 'research',
  ];
  if (softSkillComps.includes(parsed.competency)) return 'soft-skill';
  return null;
};

const parseIntStrict = (v: any): number | null => {
  if (typeof v === 'number' && !isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseInt(v, 10);
    if (!isNaN(n)) return n;
  }
  return null;
};

const toBoolean = (v: any): boolean => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const lower = v.toLowerCase();
    return lower === 'true' || lower === 'yes';
  }
  return Boolean(v);
};

const isEmptyValue = (value: any): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && (value.length === 0 || value.every((v: any) => v == null))) ||
  (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

// Work-style option values (e.g. "Prioritizing") → payload keys.
const WORK_STYLE_VALUE_TO_KEY: Record<string, string> = {
  prioritizing: 'prioritizing',
  visualizing: 'visualizing',
  coordinating: 'coordinating',
  planning: 'planning',
};

const normalizeWorkStyleCompetency = (competency: string): string => {
  const baseCompetency = competency.replace(/_(entry|associate|mid|senior)$/, '');
  return baseCompetency === 'priority' ? 'prioritizing' : baseCompetency;
};

export interface BuildPayloadsOptions {
  minutesLeft?: number;
}

/**
 * Transform form values into per-section grouped payloads.
 * One bucket per section; omits empty sections.
 */
export const buildSectionedPayloads = (
  formValues: Record<string, any>,
  questions: QuestionWithField[],
  opts: BuildPayloadsOptions = {}
): Partial<Record<SectionSlug, Record<string, any>>> => {
  const sectionPayloads: Partial<Record<SectionSlug, any>> = {};
  const domainEntries: Array<{ question: number | string; response: any }> = [];

  const ensureBucket = (section: SectionSlug): Record<string, any> => {
    if (!sectionPayloads[section]) sectionPayloads[section] = {};
    return sectionPayloads[section]!;
  };

  const extractScore = (question: QuestionWithField, value: any): number | null => {
    // QuestionRanking renders scale options as checkbox-group inputs, which
    // makes react-hook-form store the value as a single-element array even
    // though the UI is single-select. Unwrap before any scalar handling.
    if (Array.isArray(value) && value.length === 1) value = value[0];
    if (typeof value === 'object' && value !== null && 'score' in value) {
      return parseIntStrict((value as any).score);
    }
    if (question.options && question.options.length > 0) {
      const opt = question.options.find(
        (o: any) =>
          o.text === value ||
          o.label === value ||
          o.value === value ||
          String(o.score) === String(value)
      );
      if (opt?.score !== undefined) return parseIntStrict(opt.score);
    }
    return parseIntStrict(value);
  };

  const extractGroupSlug = (field: string): string | null => {
    const m = field.match(/^(.+?)_q\d+(?:_|$)/);
    return m ? m[1] : null;
  };

  // Work-motivators and work-qualities use a grouped array contract:
  //   { motivators: [{ name, question_1, question_2 }, ...] }
  //   { qualities:  [{ name, question_1, question_2 }, ...] }
  // Build per-group entries in declared order, then attach to the section bucket.
  const buildGroupedSection = (section: SectionSlug, listKey: 'motivators' | 'qualities') => {
    const order: string[] = [];
    const byName = new Map<string, Record<string, any>>();

    getSectionQuestions(questions, section).forEach((question) => {
      if (!question.field) return;
      const groupSlug = extractGroupSlug(question.field);
      const name = normalizeSlugValue(groupSlug);
      if (!name) return;

      const qNumMatch = question.field.match(/_q(\d+)/);
      const qNum = qNumMatch ? parseInt(qNumMatch[1], 10) : null;
      if (qNum === null) return;

      const formKey = `question_${question.id}`;
      const value = formValues[formKey];
      if (isEmptyValue(value)) return;

      const numValue = extractScore(question, value);
      if (numValue === null) return;

      if (!byName.has(name)) {
        byName.set(name, { name });
        order.push(name);
      }
      byName.get(name)![`question_${qNum}`] = numValue;
    });

    if (order.length === 0) return;
    const bucket = ensureBucket(section);
    bucket[listKey] = order.map((n) => byName.get(n)!);
  };

  buildGroupedSection('work-motivators', 'motivators');
  buildGroupedSection('work-qualities', 'qualities');

  questions.forEach((question) => {
    if (!question.field) return;

    const formKey = `question_${question.id}`;
    const toolsFlow = question.type === 'productivity_tools' || question.type === 'skill_rating';
    const value = toolsFlow ? (formValues.tools ?? formValues[formKey]) : formValues[formKey];
    const ratingValue = formValues[`${formKey}Rating`];

    const section = question.section || inferSectionFromField(question.field);
    if (!section) return;

    // Work-motivators and work-qualities are fully assembled in the pre-pass above.
    if (section === 'work-motivators' || section === 'work-qualities') return;

    if (!toolsFlow && isEmptyValue(value)) return;

    const normalizedField = normalizeFieldForAPI(question.field);
    const qKey = `question${parseField(normalizedField)?.qNum ?? ''}`;

    if (section === 'personality') {
      const bucket = ensureBucket(section);
      if (question.type === 'statement_rating' || question.type === 'single_choice_with_rating') {
        const rawOpt = Array.isArray(value) ? value[0] : value;
        const idx = findOptionIndex(question.options, rawOpt);
        const letter = idx >= 0 ? indexToLowerLetter(idx) : null;
        if (!bucket[qKey] || typeof bucket[qKey] !== 'object' || Array.isArray(bucket[qKey])) {
          bucket[qKey] = {};
        }
        if (letter) bucket[qKey].option = letter;
        const r = parseIntStrict(ratingValue);
        if (r !== null) bucket[qKey].rating = r;
        return;
      }

      if (question.type === 'scale' || question.type === 'rating' || question.type === 'scale_with_options') {
        const numValue = extractScore(question, value);
        if (numValue !== null) {
          bucket[qKey] = numValue;
        }
        return;
      }
    }

    if (section === 'work-style' && question.type !== 'ranking' && question.type !== 'vertical_ranking') {
      const parsedWorkStyle = parseField(normalizedField);
      if (parsedWorkStyle) {
        const bucket = ensureBucket(section);
        const competency = normalizeWorkStyleCompetency(parsedWorkStyle.competency);
        if (!bucket[competency]) bucket[competency] = {};
        bucket[competency][`question${parsedWorkStyle.qNum}`] = parseIntStrict(value) ?? value;
      }
      return;
    }

    // Basic-expectations: scale questions → { expectation: scale_value }.
    if (section === 'basic-expectations') {
      const bucket = ensureBucket(section);
      const expectation = question.field.replace('basic_expectations_', '');
      bucket[expectation] = Number(value);
      return;
    }

    if (section === 'domain-knowledge') {
      const qId = typeof question.backendId === 'number' ? question.backendId : parseIntStrict(question.backendId);
      if (qId === null) return;
      const response = typeof value === 'string' ? value.trim() : value;
      if (isEmptyValue(response)) return;
      domainEntries.push({ question: qId, response });
      return;
    }

    // Gate questions (yes/no for leadership, persuasion) live at `competency.option`.
    if (question.type === 'yes_no' || question.type === 'true_false') {
      const gateMatch = normalizedField.match(/^(.+)_(experience|gate)$/);
      const gateComp = gateMatch ? gateMatch[1] : normalizedField.replace(/_q\d+(_.*)?$/, '');
      const resolvedGateComp = resolveCompetencyKey(gateComp);
      const bucket = ensureBucket(section);
      if (!bucket[resolvedGateComp]) bucket[resolvedGateComp] = {};
      bucket[resolvedGateComp].option = toBoolean(value);
      return;
    }

    const parsed = parseField(normalizedField);
    if (!parsed) return;

    const { competency, qNum, answerType } = parsed;
    const bucket = ensureBucket(section);

    const isCompetencyNested = section === 'soft-skill';

    let container: Record<string, any>;
    if (isCompetencyNested) {
      const resolvedComp = resolveCompetencyKey(competency);
      if (!bucket[resolvedComp]) bucket[resolvedComp] = {};
      container = bucket[resolvedComp];
    } else {
      container = bucket;
    }

    switch (question.type) {
      case 'statement_rating':
      case 'single_choice_with_rating': {
        const rawOpt = Array.isArray(value) ? value[0] : value;
        const idx = findOptionIndex(question.options, rawOpt);
        const letter = idx >= 0 ? indexToLetter(idx) : null;
        if (!container[qKey] || typeof container[qKey] !== 'object' || Array.isArray(container[qKey])) {
          container[qKey] = {};
        }
        if (letter) container[qKey].option = letter;
        const r = parseIntStrict(ratingValue);
        if (r !== null) container[qKey].rating = r;
        break;
      }

      case 'multiple_choice': {
        if (answerType === 'rating') {
          const opt = question.options?.find(
            (o: any) => o.text === value || o.label === value || o.value === value
          );
          const score = opt?.score ?? parseIntStrict(value);
          if (score !== null && score !== undefined) container[qKey] = score;
        } else {
          const idx = findOptionIndex(question.options, value);
          container[qKey] = idx >= 0 ? indexToLetter(idx) : value;
        }
        break;
      }

      case 'scale':
      case 'rating':
      case 'scale_with_options': {
        const numValue = extractScore(question, value);
        if (numValue !== null) container[qKey] = numValue;
        break;
      }

      case 'ranking':
      case 'vertical_ranking': {
        if (!Array.isArray(value)) break;
        if ((section as any) === 'work-style') {
          const workStyleBucket = ensureBucket(section);
          const parsedWorkStyle = parseField(normalizedField);
          const competency = normalizeWorkStyleCompetency(parsedWorkStyle?.competency || normalizedField);
          if (!workStyleBucket[competency]) workStyleBucket[competency] = {};
          const competencyBucket = workStyleBucket[competency];
          const workStyleKey = `question${parsedWorkStyle?.qNum ?? 1}`;
          if (!competencyBucket[workStyleKey] || typeof competencyBucket[workStyleKey] !== 'object' || Array.isArray(competencyBucket[workStyleKey])) {
            competencyBucket[workStyleKey] = {};
          }
          const obj = competencyBucket[workStyleKey];
          value.forEach((item: any, rankIdx: number) => {
            if (item == null) return;
            const optValue =
              typeof item === 'object' ? item.value || item.text || item.label : String(item);
            if (!optValue) return;
            const key = WORK_STYLE_VALUE_TO_KEY[String(optValue).toLowerCase()] || String(optValue).toLowerCase();
            obj[key] = rankIdx + 1;
          });
        } else {
          const itemsCount = question.items?.length || question.options?.length || value.length;
          const ranking: number[] = new Array(itemsCount).fill(0);
          value.forEach((item: any, rankIdx: number) => {
            if (item == null) return;
            let originalIdx: number;
            if (typeof item === 'object') {
              const pos = item.position ?? item.rank ?? rankIdx + 1;
              originalIdx = Number(pos) - 1;
            } else if (typeof item === 'number') {
              originalIdx = item - 1;
            } else {
              const n = parseInt(String(item), 10);
              originalIdx = isNaN(n) ? rankIdx : n - 1;
            }
            if (originalIdx >= 0 && originalIdx < itemsCount) {
              ranking[originalIdx] = rankIdx + 1;
            }
          });
          container[qKey] = ranking;
        }
        break;
      }

      case 'multiple_rating': {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          const obj: Record<string, number> = {};
          Object.entries(value).forEach(([k, v]) => {
            const n = parseIntStrict(v);
            if (n !== null) obj[k] = n;
          });
          container[qKey] = obj;
        } else if (Array.isArray(value)) {
          const obj: Record<string, number> = {};
          value.forEach((v, i) => {
            const n = parseIntStrict(v);
            if (n !== null) obj[indexToLetter(i)] = n;
          });
          container[qKey] = obj;
        }
        break;
      }

      case 'productivity_tools':
      case 'skill_rating': {
        const toolsData = formValues.tools || value;
        if (!Array.isArray(toolsData)) break;
        const formatted = toolsData
          .filter((item: any) => item && (item.tool || item.name))
          .map((item: any) => {
            let rating = 1;
            const profValue = item.proficiency?.value ?? item.proficiency ?? item.rating;
            if (typeof profValue === 'string') {
              const profMap: Record<string, number> = {
                beginner: 2,
                intermediate: 4,
                advanced: 6,
                expert: 8,
                master: 10,
              };
              rating = profMap[profValue.toLowerCase()] || parseInt(profValue, 10) || 1;
            } else if (typeof profValue === 'number') {
              rating = profValue;
            }
            return { tool: item.tool || item.name, rating };
          });
        container[qKey] = formatted;
        break;
      }

      case 'text':
      case 'open_ended': {
        container[qKey] = value;
        if (question.hasOthersOption && question.othersField) {
          // no-op: handled by caller if needed
        }
        break;
      }

      case 'multi_select':
      case 'multiple_select': {
        if (!Array.isArray(value)) break;
        // Soft-skill leadership multi-select expects the option.score (int), not letters.
        if (section === 'soft-skill') {
          const scores = value
            .map((item: any) => {
              const opt = question.options?.find(
                (o: any) =>
                  o.text === item ||
                  o.label === item ||
                  o.value === item ||
                  (o.score !== undefined && String(o.score) === String(item))
              );
              const s = opt?.score ?? parseIntStrict(item);
              return s !== null && s !== undefined ? Number(s) : null;
            })
            .filter((n: any): n is number => n !== null);
          container[qKey] = scores;
        } else {
          const letters = value.map((item: any) => {
            const idx = findOptionIndex(question.options, item);
            return idx >= 0 ? indexToLetter(idx) : item;
          });
          container[qKey] = letters;
        }
        break;
      }

      case 'matching':
      default:
        container[qKey] = value;
    }
  });

  if (domainEntries.length > 0) {
    sectionPayloads['domain-knowledge'] = {
      domain_knowledge: domainEntries,
    };
  }

  // Backend requires `minutes_left` on every section payload.
  const minutesLeft = opts.minutesLeft ?? 0;
  (Object.keys(sectionPayloads) as SectionSlug[]).forEach((section) => {
    const body = sectionPayloads[section];
    if (body && typeof body === 'object') {
      body.minutes_left = minutesLeft;
    }
  });

  return sectionPayloads;
};

export const saveFormStateToLocalStorage = (
  formValues: Record<string, any>,
  _questionIds?: string[]
) => {
  const stateToSave: Record<string, any> = {};
  Object.keys(formValues).forEach((key) => {
    const value = formValues[key];
    if (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0) &&
      !(typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
    ) {
      stateToSave[key] = value;
    }
  });
  if (formValues.tools && Array.isArray(formValues.tools) && formValues.tools.length > 0) {
    const hasContent = formValues.tools.some((t: any) => t && (t.tool || t.proficiency?.value));
    if (hasContent) stateToSave.tools = formValues.tools;
  }
  if (Object.keys(stateToSave).length > 0) {
    localStorage.setItem('assessment_form_state', JSON.stringify(stateToSave));
  }
};

export const loadFormStateFromLocalStorage = (): Record<string, any> | null => {
  const saved = localStorage.getItem('assessment_form_state');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch {
      return null;
    }
  }
  return null;
};

export const clearFormStateFromLocalStorage = () => {
  localStorage.removeItem('assessment_form_state');
  localStorage.removeItem('secondsLeft');
};
